import { DataTable, DataTableMessageRow } from 'components/DataTable'
import { Button } from 'components/Button'
import { DraftMessage } from 'types'
import moment from 'moment'
import { sendAllMessageStrings } from '../../../constants'
import { DATE_FORMAT } from '../../../constants'
import { TAutoReplyMessageAsset } from 'api'
import { getDraftPreviewLabel } from './draftPreview'

type DraftsTableProps = {
  drafts: DraftMessage[]
  assets: TAutoReplyMessageAsset[]
  loading: boolean
  onApply: (draftId: string) => void
  onDelete: (draftId: string) => void
  onPreview: (draft: DraftMessage, e?: React.MouseEvent) => void
}

export function DraftsTable({
  drafts,
  assets,
  loading,
  onApply,
  onDelete,
  onPreview,
}: DraftsTableProps) {
  let body: JSX.Element | JSX.Element[]

  if (loading) {
    body = (
      <DataTableMessageRow
        colSpan={6}
        message={sendAllMessageStrings.drafts.loading}
      />
    )
  } else if (drafts.length === 0) {
    body = (
      <DataTableMessageRow
        colSpan={6}
        message={sendAllMessageStrings.drafts.empty}
      />
    )
  } else {
    body = drafts.map((draft) => (
      <DraftsTableRow
        key={draft.id}
        draft={draft}
        onApply={onApply}
        onDelete={onDelete}
        onPreview={onPreview}
        getDraftPreview={(item) => getDraftPreviewLabel(item, assets)}
      />
    ))
  }

  return (
    <DataTable
      minWidthClassName="min-w-[1200px]"
      tableLayout="auto"
      headerRow={
        <tr>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.drafts.table.actions}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.drafts.table.name}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.drafts.table.preview}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.drafts.table.sectionCount}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.drafts.table.createdAt}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {sendAllMessageStrings.drafts.table.updatedAt}
          </th>
        </tr>
      }
    >
      {body}
    </DataTable>
  )
}

type DraftsTableRowProps = {
  draft: DraftMessage
  onApply: (draftId: string) => void
  onDelete: (draftId: string) => void
  onPreview: (draft: DraftMessage, e?: React.MouseEvent) => void
  getDraftPreview: (draft: DraftMessage) => string
}

function DraftsTableRow({
  draft,
  onApply,
  onDelete,
  onPreview,
  getDraftPreview,
}: DraftsTableRowProps) {
  return (
    <tr className="border-t border-border/60 bg-white">
      <td className="px-2 py-3 text-left whitespace-nowrap">
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onPreview(draft)}
          >
            {sendAllMessageStrings.drafts.actions.preview}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => onApply(draft.id)}
          >
            {sendAllMessageStrings.drafts.actions.apply}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="text-danger"
            onClick={() => onDelete(draft.id)}
          >
            {sendAllMessageStrings.drafts.actions.delete}
          </Button>
        </div>
      </td>
      <td className="px-2 py-3 text-[11px] font-semibold text-ink sm:text-sm">
        {`${sendAllMessageStrings.drafts.preview.defaultName} ${moment(draft.createdAt).format(`${DATE_FORMAT} HH:mm`)}`}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <div className="max-w-md truncate" title={getDraftPreview(draft)}>
          {getDraftPreview(draft)}
        </div>
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {draft.sections.length}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {moment(draft.createdAt).format(`${DATE_FORMAT} HH:mm`)}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {moment(draft.updatedAt).format(`${DATE_FORMAT} HH:mm`)}
      </td>
    </tr>
  )
}
